import React from "react"
import { User } from "firebase/auth"
import { useForm } from "react-hook-form"
import { SpaceKey, UserId } from "popopo-msg"
import { api, firestore } from "../../core"
import { useToast } from "popopo-lib-front/hook"
import { useSpaceUser } from "popopo-lib-front/firestore-access"
import { useAutoUpdateConnection } from "../../lib/useAutoUpdateConnection"

type SpaceConnectionForms = {
  userId: string
}

export const SpaceConnectionForm = (props: { firebase: User; spaceKey: SpaceKey }) => {
  const toast = useToast()
  const [connectionInfo, setConnectionInfo] = React.useState<string>()
  const currentUserId = UserId(props.firebase.uid)
  const spaceUser = useSpaceUser(firestore, props.spaceKey, currentUserId)

  const form = useForm<SpaceConnectionForms>({
    mode: "onBlur",
  })
  const { isSubmitting } = form.formState

  const onConnectionInfo = async () => {
    try {
      const idToken = await props.firebase.getIdToken()
      const res = await api.POST("/api/v2/spaces/{space_key}/connection-info", {
        params: { path: { space_key: props.spaceKey } },
        headers: {
          authorization: `Bearer ${idToken}`,
        },
      })
      if (res.data == null) {
        toast.add({ type: "danger", title: "Connection", message: JSON.stringify(res.error) })
      } else {
        // 便利のために TRTC 接続に必要な情報全部追加してる
        const data = {
          roomId: props.spaceKey,
          userId: props.firebase.uid,
          ...res.data,
        }
        setConnectionInfo(JSON.stringify(data, null, 2))
      }
    } catch (e) {
      console.error(e)
    }
  }

  const updateLastConnectedAt = async () => {
    const idToken = await props.firebase.getIdToken()
    const res = await api.PUT("/api/v2/spaces/{space_key}/users/me/connection/last-connected-at", {
      params: { path: { space_key: props.spaceKey } },
      headers: {
        authorization: `Bearer ${idToken}`,
      },
    })
    if (res.error != null) {
      throw new Error(JSON.stringify(res.error))
    }
  }

  const onConnect = async () => {
    try {
      const idToken = await props.firebase.getIdToken()

      if (spaceUser !== "loading" && spaceUser && spaceUser.status === "online") {
        await updateLastConnectedAt()
        toast.add({ type: "success", title: "Connection", message: "Last connected at updated successfully" })
      } else {
        await api.POST("/api/v2/spaces/{space_key}/users/me/connection", {
          params: { path: { space_key: props.spaceKey } },
          headers: {
            authorization: `Bearer ${idToken}`,
          },
          body: {
            muted: false,
          },
        })
        toast.add({ type: "success", title: "Connection", message: "Connected successfully" })
      }
    } catch (e) {
      console.error(e)
      toast.add({ type: "danger", title: "Connection", message: `Error: ${JSON.stringify(e)}` })
    }
  }

  const isOnline = spaceUser !== "loading" && spaceUser != null && spaceUser.status === "online"

  const { enabled: autoUpdate, setEnabled: setAutoUpdate, lastSentAt: autoUpdateLastSentAt } = useAutoUpdateConnection({
    onUpdate: updateLastConnectedAt,
    onError: (e) => {
      console.error(e)
      toast.add({
        type: "danger",
        title: "Auto Update Connection",
        message: `自動更新に失敗したため停止しました: ${e instanceof Error ? e.message : JSON.stringify(e)}`,
      })
    },
  })

  const onDisconnect = async () => {
    try {
      const idToken = await props.firebase.getIdToken()
      await api.DELETE("/api/v2/spaces/{space_key}/users/me/connection", {
        params: { path: { space_key: props.spaceKey } },
        headers: {
          authorization: `Bearer ${idToken}`,
        },
      })
    } catch (e) {
      console.error(e)
    }
  }

  const onKick = async (value: SpaceConnectionForms) => {
    try {
      const idToken = await props.firebase.getIdToken()
      await api.DELETE("/api/v2/spaces/{space_key}/users/{user_id}/connection", {
        params: { path: { space_key: props.spaceKey, user_id: value.userId } },
        headers: {
          authorization: `Bearer ${idToken}`,
        },
      })
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <>
      <form className="d-flex flex-column gap-3 w-100">
        <button type="button" className="btn btn-primary form-control" onClick={form.handleSubmit(onConnectionInfo)}>
          ConnectionInfoを取得してTRTC接続情報を表示する
        </button>
        {connectionInfo && (
          <div className="mb-4">
            <pre>
              <code>{connectionInfo}</code>
            </pre>
            <a href={`/trtc-tester?connectionInfo=${encodeURIComponent(connectionInfo)}`} target="_blank" rel="noreferrer">
              TRTCテスターを開く
            </a>
          </div>
        )}

        {spaceUser !== "loading" && spaceUser && (
          <div className="alert alert-info">
            <strong>Current Status:</strong> {spaceUser.status}
            {spaceUser.last_connected_at && (
              <div><strong>Last Connected:</strong> {new Date(spaceUser.last_connected_at).toLocaleString()}</div>
            )}
          </div>
        )}

        <button type="button" className="btn btn-primary form-control" onClick={form.handleSubmit(onConnect)}>
          {isOnline
            ? "Update Connection"
            : "Connect"
          }
        </button>
        <div className="form-check form-switch">
          <input
            id="auto-update-connection"
            type="checkbox"
            className="form-check-input"
            checked={autoUpdate}
            onChange={e => setAutoUpdate(e.target.checked)}
            disabled={!isOnline}
          />
          <label htmlFor="auto-update-connection" className="form-check-label">
            Auto Update Connection (10s 間隔)
          </label>
          {autoUpdate && autoUpdateLastSentAt != null && (
            <small className="d-block text-muted">
              最終送信: {new Date(autoUpdateLastSentAt).toLocaleTimeString()}
            </small>
          )}
        </div>
        <button type="button" className="btn btn-danger form-control" onClick={form.handleSubmit(onDisconnect)}>
          Disconnect
        </button>

        <div className="form-floating">
          <input className="form-control" {...form.register("userId")} disabled={isSubmitting} />
          <label>User ID</label>
        </div>
        <button type="button" className="btn btn-danger form-control" onClick={form.handleSubmit(onKick)}>
          Kick other user
        </button>
      </form>
    </>
  )
}
